// src/utils/dateUtils.ts

// Add this helper function to properly parse UTC dates
export function parseUTCDate(dateString: string): Date {
  // If the date string doesn't end with 'Z', assume it's UTC and add it
  if (!dateString.endsWith('Z') && !dateString.includes('+') && !dateString.includes('-', 10)) {
    return new Date(dateString + 'Z');
  }
  return new Date(dateString);
}

/**
 * Converts a date to a human-readable "time ago" format
 * @param date - The date to convert
 * @returns A string like "5 minutes ago" or null for invalid dates
 */
export const getTimeAgo = (date?: Date | string | null): string | null => {
    if (!date) return null;
    
    const dateObj = typeof date === 'string' ? parseUTCDate(date) : date;

    // Check for invalid dates (DateTime.MinValue, etc.)
    if (dateObj.getFullYear() <= 1 || isNaN(dateObj.getTime())) {
        return null;
    }
    
    const seconds = Math.floor((new Date().getTime() - dateObj.getTime()) / 1000);
    
    if (seconds < 60) {
        return seconds === 1 ? '1 second ago' : `${seconds} seconds ago`;
    }
    
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
        return minutes === 1 ? '1 minute ago' : `${minutes} minutes ago`;
    }
    
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
        return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
    }
    
    const days = Math.floor(hours / 24);
    if (days < 30) {
        return days === 1 ? '1 day ago' : `${days} days ago`;
    }
    
    const months = Math.floor(days / 30);
    if (months < 12) {
        return months === 1 ? '1 month ago' : `${months} months ago`;
    }
    
    const years = Math.floor(months / 12);
    return years === 1 ? '1 year ago' : `${years} years ago`;
};

/**
 * Formats a time string (HH:MM:SS) to a more readable format
 * @param time - Time string in HH:MM:SS format
 * @returns Formatted time string
 */
export const formatTime = (time: string): string => {
    if (!time) return 'Not set';
    return new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
    });
};

/**
 * Formats a date to show relative time for recent dates or absolute date for older ones
 * @param date - The date to format
 * @returns Formatted date string
 */
export const formatDate = (date: Date | string): string => {
    const dateObj = typeof date === 'string' ? parseUTCDate(date) : date;
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - dateObj.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours === 1) return '1 hour ago';
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays === 1) return '1 day ago';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    
    // For dates older than a week, show the actual date
    return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
};

/**
 * Formats a duration in seconds to MM:SS or HH:MM:SS format
 * @param seconds - Duration in seconds
 * @returns Formatted duration string
 */
export const formatDuration = (seconds?: number): string => {
    if (!seconds || seconds === 0) return 'N/A';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    
    if (hours > 0) {
        return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    }
    
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

/**
 * Checks if a date is considered "online" (within the last 5 minutes)
 * @param lastSeen - The last seen date
 * @returns true if online, false otherwise
 */
export const isOnline = (lastSeen?: Date | string | number | null): boolean => {
    if (!lastSeen) return false;
    
    let dateObj: Date | null;
    
    if (typeof lastSeen === 'string') {
        dateObj = parseUTCDate(lastSeen);
    } else if (typeof lastSeen === 'number') {
        dateObj = new Date(lastSeen < 1e12 ? lastSeen * 1000 : lastSeen);
    } else {
        dateObj = lastSeen;
    }
    
    if (!dateObj || isNaN(dateObj.getTime())) {
        return false;
    }
    
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
    return dateObj > fiveMinutesAgo;
};